import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  action: () => void;
  description: string;
  category?: string;
}

export interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
}

export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true,
  preventDefault = true,
  stopPropagation = true
}: UseKeyboardShortcutsOptions) => {
  const shortcutsRef = useRef<KeyboardShortcut[]>(shortcuts);
  
  // Update shortcuts ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;
    
    // Don't trigger shortcuts when typing in inputs (unless specifically intended)
    const target = event.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' ||
                   target.tagName === 'TEXTAREA' ||
                   target.contentEditable === 'true';
    for (const shortcut of shortcutsRef.current) {
      const keyMatches = shortcut.key.toLowerCase() === event.key.toLowerCase();
      const ctrlMatches = !!shortcut.ctrlKey === event.ctrlKey;
      const shiftMatches = !!shortcut.shiftKey === event.shiftKey;
      const altMatches = !!shortcut.altKey === event.altKey;

      if (keyMatches && ctrlMatches && shiftMatches && altMatches) {
        // Allow certain shortcuts in inputs (like Ctrl+S)
        if (isInput && !shortcut.ctrlKey && !shortcut.altKey) {
          continue;
        }
        // Never intercept Tab/Shift+Tab — Tab is the browser's primary navigation key
        // and must never be preventDefault'd globally (regardless of what element has focus)
        if (shortcut.key.toLowerCase() === 'tab') {
          continue;
        }
        
        if (preventDefault) {
          event.preventDefault();
        }
        
        if (stopPropagation) {
          event.stopPropagation();
        }
        
        shortcut.action();
        break;
      }
    }
  }, [enabled, preventDefault, stopPropagation]);

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enabled]);
};

// Global shortcuts for the application
export const createGlobalShortcuts = (
  saveDraft: () => void,
  addNewRow: () => void,
  removeSelectedRow: () => void,
  nextField: () => void,
  previousField: () => void,
  showHelp: () => void
): KeyboardShortcut[] => [
  {
    key: 's',
    ctrlKey: true,
    action: saveDraft,
    description: 'Save draft',
    category: 'General'
  },
  {
    key: 'Tab',
    action: nextField,
    description: 'Next field',
    category: 'Navigation'
  },
  {
    key: 'Tab',
    shiftKey: true,
    action: previousField,
    description: 'Previous field',
    category: 'Navigation'
  },
  {
    key: 'Enter',
    ctrlKey: true,
    action: addNewRow,
    description: 'Add new row',
    category: 'Table'
  },
  {
    key: 'Delete',
    action: removeSelectedRow,
    description: 'Remove selected row',
    category: 'Table'
  },
  {
    key: 'F1',
    action: showHelp,
    description: 'Show help',
    category: 'General'
  },
  {
    key: '?',
    shiftKey: true,
    action: showHelp,
    description: 'Show keyboard shortcuts',
    category: 'General'
  }
];

// Table-specific shortcuts
export const createTableShortcuts = (
  addRow: () => void,
  deleteRow: (index: number) => void,
  duplicateRow: (index: number) => void,
  moveRowUp: (index: number) => void,
  moveRowDown: (index: number) => void,
  selectAll: () => void,
  currentRowIndex: number
): KeyboardShortcut[] => [
  {
    key: 'n',
    ctrlKey: true,
    action: addRow,
    description: 'Add new row',
    category: 'Table'
  },
  {
    key: 'd',
    ctrlKey: true,
    action: () => duplicateRow(currentRowIndex),
    description: 'Duplicate current row',
    category: 'Table'
  },
  {
    key: 'Backspace',
    ctrlKey: true,
    action: () => deleteRow(currentRowIndex),
    description: 'Delete current row',
    category: 'Table'
  },
  {
    key: 'ArrowUp',
    ctrlKey: true,
    shiftKey: true,
    action: () => moveRowUp(currentRowIndex),
    description: 'Move row up',
    category: 'Table'
  },
  {
    key: 'ArrowDown',
    ctrlKey: true,
    shiftKey: true,
    action: () => moveRowDown(currentRowIndex),
    description: 'Move row down',
    category: 'Table'
  },
  {
    key: 'a',
    ctrlKey: true,
    action: selectAll,
    description: 'Select all rows',
    category: 'Table'
  }
];

// Form-specific shortcuts
export const createFormShortcuts = (
  goToNextSection: () => void,
  goToPreviousSection: () => void,
  validateForm: () => void,
  resetForm: () => void
): KeyboardShortcut[] => [
  {
    key: 'ArrowRight',
    ctrlKey: true,
    action: goToNextSection,
    description: 'Next section',
    category: 'Navigation'
  },
  {
    key: 'ArrowLeft',
    ctrlKey: true,
    action: goToPreviousSection,
    description: 'Previous section',
    category: 'Navigation'
  },
  {
    key: 'Enter',
    ctrlKey: true,
    shiftKey: true,
    action: validateForm,
    description: 'Validate form',
    category: 'Form'
  },
  {
    key: 'r',
    ctrlKey: true,
    shiftKey: true,
    action: resetForm,
    description: 'Reset form',
    category: 'Form'
  }
];

// Calculation shortcuts
export const createCalculationShortcuts = (
  startCalculation: () => void,
  viewResults: () => void,
  exportResults: () => void
): KeyboardShortcut[] => [
  {
    key: 'c',
    ctrlKey: true,
    shiftKey: true,
    action: startCalculation,
    description: 'Start calculation',
    category: 'Calculation'
  },
  {
    key: 'r',
    ctrlKey: true,
    action: viewResults,
    description: 'View results',
    category: 'Calculation'
  },
  {
    key: 'e',
    ctrlKey: true,
    action: exportResults,
    description: 'Export results',
    category: 'Calculation'
  }
];

// Hook for managing focus navigation
export const useFocusNavigation = () => {
  const getFocusableElements = (): HTMLElement[] => {
    const selectors = [
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ];
    
    return Array.from(document.querySelectorAll(selectors.join(', '))) as HTMLElement[];
  };

  const focusNext = useCallback(() => {
    const focusableElements = getFocusableElements();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    focusableElements[nextIndex]?.focus();
  }, []);

  const focusPrevious = useCallback(() => {
    const focusableElements = getFocusableElements();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const prevIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    focusableElements[prevIndex]?.focus();
  }, []);

  const focusFirst = useCallback(() => {
    const focusableElements = getFocusableElements();
    focusableElements[0]?.focus();
  }, []);

  const focusLast = useCallback(() => {
    const focusableElements = getFocusableElements();
    focusableElements[focusableElements.length - 1]?.focus();
  }, []);

  return {
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
    getFocusableElements
  };
};

// Hook for table navigation
export const useTableNavigation = (
  tableRef: React.RefObject<HTMLTableElement>,
  rows: number,
  columns: number
) => {
  const navigateCell = useCallback((rowDelta: number, colDelta: number) => {
    if (!tableRef.current) return;
    
    const activeElement = document.activeElement as HTMLElement;
    const cell = activeElement.closest('td, th');
    if (!cell) return;
    
    const parentRow = cell.parentElement as HTMLTableRowElement;
    const currentRow = Array.from(tableRef.current.rows).indexOf(parentRow);
    const currentCol = Array.from(parentRow.cells).indexOf(cell as HTMLTableCellElement);
    
    const newRow = Math.max(0, Math.min(rows - 1, currentRow + rowDelta));
    const newCol = Math.max(0, Math.min(columns - 1, currentCol + colDelta));
    
    const targetCell = tableRef.current.rows[newRow]?.cells[newCol];
    const input = targetCell?.querySelector('input, select, textarea, button') as HTMLElement;
    
    if (input) {
      input.focus();
    } else {
      targetCell?.focus();
    }
  }, [tableRef, rows, columns]);

  const createTableShortcuts = useCallback((): KeyboardShortcut[] => [
    {
      key: 'ArrowUp',
      action: () => navigateCell(-1, 0),
      description: 'Move up in table',
      category: 'Table Navigation'
    },
    {
      key: 'ArrowDown',
      action: () => navigateCell(1, 0),
      description: 'Move down in table',
      category: 'Table Navigation'
    },
    {
      key: 'ArrowLeft',
      action: () => navigateCell(0, -1),
      description: 'Move left in table',
      category: 'Table Navigation'
    },
    {
      key: 'ArrowRight',
      action: () => navigateCell(0, 1),
      description: 'Move right in table',
      category: 'Table Navigation'
    }
  ], [navigateCell]);

  return {
    navigateCell,
    createTableShortcuts
  };
};